
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Navbar from './components/Navbar';
import ArtCard from './components/ArtCard';
import CustomRequest from './components/CustomRequest';
import ImageModal from './components/ImageModal';
import MouseParticles from './components/MouseParticles';
import AdminPanel from './components/AdminPanel';
import AdminLogin from './components/AdminLogin';
import UserAuthModal from './components/UserAuthModal';
import SaveIndicator from './components/SaveIndicator';
import UserOrders from './components/UserOrders';
import UserProfile from './components/UserProfile';
import ChatOverlay from './components/ChatOverlay';
import CommentsSection from './components/CommentsSection';
import { database } from './services/databaseService';
import { storage } from './services/storageService';
import { ArtItem, CartItem, ArtCategory, Language, Order, OrderStatus, ChatThread, ChatMessage, Comment, User } from './types';
import { INITIAL_ARTS, DATA_VERSION, DEFAULT_CATEGORY_COVERS } from './constants';
import { Flower, Settings, ArrowRight, Loader2, Search, Trash2, X, ShoppingBag, CheckCircle, CloudDownload, Sparkles, RefreshCw, Server, ShieldCheck, Lock, Unlock, PlayCircle, LayoutGrid } from 'lucide-react';

const App: React.FC = () => {
  // Função para pegar a página inicial baseada no Hash da URL
  const getInitialPageFromHash = () => {
    try {
      const hash = window.location.hash.replace('#', '');
      const page = hash.split('/')[0];
      const validPages = ['home', 'gallery', 'custom-art', 'my-orders', 'profile'];
      return validPages.includes(page) ? page : 'home';
    } catch (e) {
      return 'home';
    }
  };

  const [activePage, setActivePage] = useState(getInitialPageFromHash());
  const [arts, setArts] = useState<ArtItem[]>([]); 
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedArt, setSelectedArt] = useState<ArtItem | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ArtCategory | null>(null);
  const [categoryCovers, setCategoryCovers] = useState<Record<string, string>>(DEFAULT_CATEGORY_COVERS);
  const [language, setLanguage] = useState<Language>('en'); // Idioma inicial trocado para Inglês
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isUserAuthOpen, setIsUserAuthOpen] = useState(false);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [isAdminLoginOpen, setIsAdminLoginOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'cloud' | 'local'>('local');
  const [notifications, setNotifications] = useState({ orders: 0, chats: 0 });

  // Monitora mudanças no Hash da URL (Botão voltar/avançar do navegador)
  useEffect(() => {
    const handleHashChange = () => {
      const newPage = getInitialPageFromHash();
      setActivePage(newPage);

      // Verificar se há uma arte específica na URL
      const hash = window.location.hash.replace('#', '');
      if (hash.includes('/')) {
        const [page, artId] = hash.split('/');
        if (page === 'gallery' && artId && arts.length > 0) {
          const art = arts.find(a => a.id === artId);
          if (art) setSelectedArt(art);
        }
      } else if (selectedArt) {
        // Se o hash mudou para algo sem barra e havia uma arte selecionada, fecha o modal
        setSelectedArt(null);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [arts, selectedArt]);

  // Sincronizar selectedArt com a URL
  useEffect(() => {
    if (isLoaded && arts.length > 0) {
      const hash = window.location.hash.replace('#', '');
      if (hash.includes('/')) {
        const [page, artId] = hash.split('/');
        if (page === 'gallery' && artId) {
          const art = arts.find(a => a.id === artId);
          if (art) setSelectedArt(art);
        }
      }
    }
  }, [isLoaded, arts]);

  // Atualiza a URL quando uma arte é selecionada ou fechada
  useEffect(() => {
    if (!isLoaded) return;
    
    const currentHash = window.location.hash.replace('#', '');
    const [page] = currentHash.split('/');
    
    if (selectedArt) {
      const newHash = `gallery/${selectedArt.id}`;
      if (currentHash !== newHash) {
        window.location.hash = newHash;
      }
    } else if (currentHash.includes('/') && page === 'gallery') {
      // Se fechou a arte e a URL ainda tem o ID, volta para gallery
      window.location.hash = 'gallery';
    }
  }, [selectedArt, isLoaded]);

  const syncData = useCallback(async () => {
    setIsSyncing(true);
    try {
      // Registrar visita (Analytics)
      await database.logVisit();

      const artsRes = await database.getArts();
      setArts(artsRes.data);
      setSyncStatus(artsRes.source === 'hostinger_server' ? 'cloud' : 'local');

      const covers = await database.getCategoryCovers();
      setCategoryCovers(covers);

      const user = await database.getCurrentUser();
      if (user) setCurrentUser(user);
      
      const [commRes, ordersRes, chatsRes] = await Promise.all([
        database.getComments(),
        database.getOrders(),
        database.getChatThreads()
      ]);
      if (commRes.data) setComments(commRes.data);
      if (ordersRes.data) setOrders(ordersRes.data);
      if (chatsRes) setChatThreads(chatsRes);
    } catch (e) {
      console.error("Critical Sync Failure:", e);
      setArts(INITIAL_ARTS);
    } finally {
      setIsLoaded(true);
      setIsSyncing(false);
    }
  }, []);

  useEffect(() => {
    syncData();
    // Safety timeout: force load after 5 seconds if sync hangs
    const timer = setTimeout(() => setIsLoaded(true), 5000);
    return () => clearTimeout(timer);
  }, [syncData]);

  useEffect(() => {
    const interval = setInterval(async () => {
      const [chats, ordersRes] = await Promise.all([
        database.getChatThreads(),
        database.getOrders()
      ]);
      
      if (chats) {
        setChatThreads(prev => {
          if (chats.length > prev.length) {
            setNotifications(n => ({ ...n, chats: n.chats + (chats.length - prev.length) }));
          } else {
            // Check if any thread has new messages
            chats.forEach(newThread => {
              const oldThread = prev.find(t => t.userEmail === newThread.userEmail);
              if (oldThread && newThread.messages.length > oldThread.messages.length) {
                const lastMsg = newThread.messages[newThread.messages.length - 1];
                if (lastMsg.sender === 'user') {
                  setNotifications(n => ({ ...n, chats: n.chats + 1 }));
                }
              }
            });
          }
          return chats;
        });
      }

      if (ordersRes.data) {
        setOrders(prev => {
          if (ordersRes.data.length > prev.length) {
            setNotifications(n => ({ ...n, orders: n.orders + (ordersRes.data.length - prev.length) }));
          }
          return ordersRes.data;
        });
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const total = notifications.orders + notifications.chats;
    if (total > 0) {
      document.title = `(${total}) Niiko Artwork`;
    } else {
      document.title = `Niiko Artwork`;
    }
  }, [notifications]);

  const isVideoUrl = (url?: string) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('blob:') || lower.startsWith('data:video/');
  };

  const handleSendMessage = useCallback(async (text: string, imageUrl?: string) => {
    if (!currentUser) return;
    
    const nm: ChatMessage = { 
      id: `USER-${Date.now()}`, 
      sender: 'user', 
      text, 
      imageUrl, 
      timestamp: new Date().toLocaleTimeString() 
    };

    const success = await database.saveChatMessage(currentUser.email, currentUser.name, nm);
    
    if (!success) {
      console.error("Failed to save message to server");
      // Opcional: alert(language === 'en' ? "Failed to send message." : "Erro ao enviar mensagem.");
    }

    setChatThreads(prev => {
      const up = [...prev];
      const idx = up.findIndex(ct => ct.userEmail === currentUser.email);
      if (idx > -1) {
        up[idx] = { ...up[idx], messages: [...up[idx].messages, nm], lastUpdated: new Date().toISOString() };
      } else {
        up.push({ userEmail: currentUser.email, userName: currentUser.name, messages: [nm], lastUpdated: new Date().toISOString(), unreadCount: 1 });
      }
      return up;
    });
  }, [currentUser]);

  const handleCheckout = async () => {
    if (!currentUser) { 
      setIsCartOpen(false); 
      setIsUserAuthOpen(true); 
      return; 
    }
    
    setIsSaving(true);
    const newOrder: Order = {
      id: `ORD-${Date.now()}`,
      userId: currentUser.email,
      userName: currentUser.name,
      userEmail: currentUser.email,
      items: [...cart],
      total: cart.reduce((a, b) => a + (b.price * b.quantity), 0),
      date: new Date().toLocaleString(),
      status: OrderStatus.PENDING
    };

    try {
      const success = await database.saveOrder(newOrder);
      if (success) {
        setOrders(prev => [newOrder, ...prev]);
        const orderSummary = cart.map(i => i.title[language]).join(', ');
        handleSendMessage(`🚀 NEW ORDER: #${newOrder.id}. Items: ${orderSummary}.`);
        setCart([]);
        setIsCartOpen(false);
        handleNavigate('my-orders');
      }
    } catch (e) {
      alert(language === 'en' ? "Error sealing order." : "Erro ao selar pedido.");
    } finally {
      setIsSaving(false);
    }
  };

  // Função centralizada para navegar e mudar a URL
  const handleNavigate = (page: string) => {
    setActivePage(page);
    try {
      window.location.hash = page; // Atualiza a URL com o Hash
    } catch (e) {
      // Silently fail if location.hash is inaccessible (e.g. in blob URLs)
      console.warn("Navigation warning: Unable to set location hash.", e);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredArts = useMemo(() => {
    return arts.filter(art => {
      const matchesCategory = categoryFilter ? art.category === categoryFilter : true;
      const title = art.title[language] || '';
      return matchesCategory && title.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [arts, categoryFilter, searchQuery, language]);

  if (!isLoaded) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center gap-12 z-[999]">
        <Flower className="w-20 h-20 text-[#ff9eb5] animate-spin" />
        <p className="text-white/80 font-bold uppercase tracking-[0.4em] text-[10px]">
          {language === 'en' ? 'Connecting to the Sanctuary...' : 'Conectando ao Santuário...'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent selection:bg-[#ff9eb5] selection:text-white relative">
      <MouseParticles />
      <SaveIndicator active={isSaving} language={language} />

      <Navbar 
        cartCount={cart.length} 
        onCartClick={() => currentUser ? setIsCartOpen(true) : setIsUserAuthOpen(true)}
        onChatClick={() => (currentUser ? setIsChatOpen(true) : setIsUserAuthOpen(true))}
        onAdminClick={() => setIsAdminLoginOpen(true)}
        onNavigate={handleNavigate}
        onUserAuthClick={() => setIsUserAuthOpen(true)}
        currentUser={currentUser}
        onLogout={() => { database.signOut(); setCurrentUser(null); handleNavigate('home'); }}
        activePage={activePage}
        language={language}
        onToggleLanguage={() => setLanguage(l => l === 'pt' ? 'en' : 'pt')}
      />

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-12 md:py-20 relative z-10">
        {activePage === 'home' && (
          <div className="space-y-48">
            <div className="flex flex-col items-center text-center space-y-12 py-10 md:py-24">
              <div className="bg-white p-8 shadow-2xl border-b-[8px] border-[#ff9eb5] animate-in zoom-in duration-1000">
                <Flower className="w-16 h-16 text-black" />
              </div>
              <h1 className="text-5xl md:text-[5.5rem] font-black text-white uppercase tracking-tighter leading-none oil-text">
                Niiko Artwork
              </h1>
              <button onClick={() => handleNavigate('gallery')} className="bg-white hover:bg-[#ff9eb5] text-black hover:text-white px-16 py-8 text-xs font-black uppercase tracking-[0.5em] transition-all flex items-center gap-8 group shadow-2xl admin-glow">
                {language === 'en' ? 'Enter Gallery' : 'Entrar na Galeria'} <ArrowRight className="w-6 h-6 group-hover:translate-x-4 transition-transform" />
              </button>
            </div>
            
            <section className="space-y-24">
              <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tighter border-b border-white/10 pb-12">New Arrivals</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-24">
                {arts.slice(0, 3).map(art => (
                  <ArtCard key={art.id} item={art} onAddToCart={() => setSelectedArt(art)} onPreview={() => setSelectedArt(art)} language={language} />
                ))}
              </div>
            </section>
          </div>
        )}

        {activePage === 'gallery' && (
          <div className="space-y-32">
            <div className="glass-panel p-8 md:p-16 flex flex-col gap-12">
               <div className="relative w-full max-w-4xl mx-auto">
                 <Search className="absolute left-10 top-1/2 -translate-y-1/2 w-8 h-8 text-zinc-500" />
                 <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={language === 'en' ? "Explore collection..." : "Explorar acervo..."} className="w-full bg-black/50 border border-zinc-800 py-8 pl-24 pr-10 text-xl text-white outline-none focus:border-[#ff9eb5] transition-all" />
               </div>
               <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-6">
                <button 
                  onClick={() => setCategoryFilter(null)} 
                  className={`relative aspect-[3/4] border-2 transition-all overflow-hidden ${!categoryFilter ? 'border-[#ff9eb5] scale-105' : 'border-white/5 opacity-40'}`}
                >
                  <LayoutGrid className="absolute inset-0 w-8 h-8 m-auto text-white z-20" />
                  <span className="absolute bottom-2 inset-x-0 flex items-center justify-center text-[10px] font-black uppercase text-white z-20">All</span>
                </button>
                {Object.values(ArtCategory).map(cat => (
                  <button key={cat} onClick={() => setCategoryFilter(cat)} className={`relative aspect-[3/4] border-2 transition-all overflow-hidden ${categoryFilter === cat ? 'border-[#ff9eb5] scale-105' : 'border-white/5 opacity-40'}`}>
                    {isVideoUrl(categoryCovers[cat] || DEFAULT_CATEGORY_COVERS[cat]) ? (
                      <video 
                        src={categoryCovers[cat] || DEFAULT_CATEGORY_COVERS[cat]} 
                        autoPlay loop muted 
                        className="absolute inset-0 w-full h-full object-cover opacity-30" 
                      />
                    ) : (
                      <img 
                        src={categoryCovers[cat] || DEFAULT_CATEGORY_COVERS[cat]} 
                        className="absolute inset-0 w-full h-full object-cover opacity-30" 
                      />
                    )}
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-black uppercase text-white z-20 text-center px-2">{cat}</span>
                  </button>
                ))}
               </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-24">
              {filteredArts.map(art => (
                <ArtCard key={art.id} item={art} onAddToCart={setSelectedArt} onPreview={setSelectedArt} language={language} />
              ))}
            </div>
          </div>
        )}

        {activePage === 'custom-art' && (
          <CustomRequest 
            language={language} 
            onOpenChat={() => currentUser ? setIsChatOpen(true) : setIsUserAuthOpen(true)} 
          />
        )}
        
        {activePage === 'my-orders' && currentUser && <UserOrders orders={orders.filter(o => o.userEmail.toLowerCase() === currentUser?.email.toLowerCase())} language={language} />}
        {activePage === 'profile' && currentUser && <UserProfile user={currentUser} orders={orders.filter(o => o.userEmail.toLowerCase() === currentUser.email.toLowerCase())} language={language} onUpdateUser={async (d) => { await database.updateUser(d); setCurrentUser(prev => prev ? {...prev, ...d} : null); return true; }} onLogout={() => { database.signOut(); setCurrentUser(null); handleNavigate('home'); }} />}
      </main>

      {selectedArt && (
        <ImageModal 
          item={selectedArt} 
          onClose={() => setSelectedArt(null)}
          onAddToCart={(it, sig, disc, steam, font, df, px, py, sc, cl) => {
            if (!currentUser) { setIsUserAuthOpen(true); return; }
            setCart(prev => [...prev, { ...it, quantity: 1, customSignature: sig, discord: disc, steamUrl: steam, customFont: font, dafontLink: df, sigPosX: px, sigPosY: py, sigScale: sc, sigColor: cl }]);
            setIsCartOpen(true);
          }}
          language={language}
        />
      )}

      {isCartOpen && currentUser && (
        <div className="fixed inset-0 z-[1000] flex justify-end animate-in fade-in duration-500">
          <div className="absolute inset-0 bg-black/95 backdrop-blur-md" onClick={() => setIsCartOpen(false)} />
          <div className="relative w-full max-w-md bg-zinc-950 border-l border-white/5 h-full flex flex-col p-8">
            <h2 className="text-xl font-black text-white uppercase mb-8">{language === 'en' ? 'Cart' : 'Carrinho'}</h2>
            <div className="flex-1 overflow-y-auto space-y-4">
              {cart.map((item, idx) => (
                <div key={idx} className="flex gap-4 p-4 bg-zinc-900 border border-white/5">
                   <div className="w-12 h-12 bg-black"><img src={item.imageUrl} className="w-full h-full object-cover" /></div>
                   <div className="flex-1"><h4 className="text-white text-[10px] uppercase font-black">{item.title[language]}</h4><p className="text-[#ff9eb5] font-mono text-xs">{language === 'en' ? `$ ${item.price.toFixed(2)}` : `R$ ${(item.price * 5).toFixed(2)}`}</p></div>
                   <button onClick={() => setCart(prev => prev.filter((_, i) => i !== idx))} className="text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="pt-8 border-t border-white/5">
               <div className="flex justify-between mb-8">
                 <span className="text-zinc-500 uppercase text-[10px] font-black">Total</span>
                 <span className="text-white text-2xl font-black">
                   {language === 'en' ? `$ ${cart.reduce((a, b) => a + (b.price * b.quantity), 0).toFixed(2)}` : `R$ ${(cart.reduce((a, b) => a + (b.price * b.quantity), 0) * 5).toFixed(2)}`}
                 </span>
               </div>
               <button onClick={handleCheckout} disabled={cart.length === 0} className="w-full bg-white hover:bg-[#ff9eb5] text-black hover:text-white py-6 font-black uppercase text-xs">
                 {language === 'en' ? 'Checkout' : 'Finalizar Pedido'}
               </button>
            </div>
          </div>
        </div>
      )}

      {isUserAuthOpen && <UserAuthModal onClose={() => setIsUserAuthOpen(false)} onLoginSuccess={(u) => { setCurrentUser(u); setIsUserAuthOpen(false); }} language={language} />}
      {isAdminLoginOpen && <AdminLogin onClose={() => setIsAdminLoginOpen(false)} onLogin={() => { setIsAdminLoginOpen(false); setIsAdminPanelOpen(true); }} />}
      {isAdminPanelOpen && (
        <AdminPanel 
          arts={arts} 
          orders={orders} 
          chatThreads={chatThreads}
          comments={comments}
          categoryCovers={categoryCovers}
          onUpdateArts={(u) => { setArts(prev => { const n = typeof u === 'function' ? u(prev) : u; storage.save('arts', n); return n; }); }}
          onUpdateOrders={setOrders}
          onUpdateSupport={() => {}}
          onUpdateChatThreads={setChatThreads}
          onUpdateComments={setComments}
          onUpdateCategoryCovers={setCategoryCovers}
          onClose={() => setIsAdminPanelOpen(false)}
          language={language}
          notifications={notifications}
          onClearNotifications={(type) => setNotifications(prev => ({ ...prev, [type]: 0 }))}
        />
      )}
      {isChatOpen && currentUser && <ChatOverlay userName={currentUser.name} messages={chatThreads.find(t => t.userEmail === currentUser.email)?.messages || []} onSendMessage={handleSendMessage} onClose={() => setIsChatOpen(false)} language={language} />}
    </div>
  );
};

export default App;
